import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Plus, Minus, Trash, PaperPlaneTilt, X, Star, Receipt, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const VARIANT_GROUPS = [
  { label: "Cebolla / Cilantro", options: ["Con todo", "Sin cebolla", "Sin cilantro", "Solo cebolla", "Solo cilantro"] },
  { label: "Salsa", options: ["Salsa aparte", "Sin salsa"] },
  { label: "Piña", options: ["Con piña", "Sin piña"] },
  { label: "Término", options: ["Bien dorado", "Poco dorado"] },
];

function pad(n) { return String(n).padStart(3, "0"); }

export default function Mesero() {
  const [tables, setTables] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTable, setActiveTable] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null); // existing order on this table (or null)
  const [activeCat, setActiveCat] = useState("Todos");
  const [cart, setCart] = useState([]); // pending NEW items to send
  const [editingItemIdx, setEditingItemIdx] = useState(null);
  const [dineOption, setDineOption] = useState("aqui");
  const [submitting, setSubmitting] = useState(false);

  const loadTables = () => api.get("/tables").then((r) => setTables(r.data)).catch(() => {});

  const loadOrderForTable = async (tableNumber) => {
    try {
      const r = await api.get(`/orders/by-table/${tableNumber}`);
      setActiveOrder(r.data || null);
    } catch {
      setActiveOrder(null);
    }
  };

  useEffect(() => {
    loadTables();
    api.get("/products", { params: { active_only: true } }).then((r) => setProducts(r.data)).catch(() => {});
    const t = setInterval(loadTables, 7000);
    return () => clearInterval(t);
  }, []);

  // poll the active order while editing (so mesero sees items being marked ready by taquero)
  useEffect(() => {
    if (!activeTable) return;
    loadOrderForTable(activeTable);
    const t = setInterval(() => loadOrderForTable(activeTable), 6000);
    return () => clearInterval(t);
  }, [activeTable]);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return ["Todos", ...Array.from(set)];
  }, [products]);

  const filteredProducts = activeCat === "Todos" ? products : products.filter((p) => p.category === activeCat);

  const newSubtotal = cart.reduce((s, it) => s + it.product.price * it.qty, 0);
  const existingTotal = activeOrder?.total ?? 0;
  const grandTotal = existingTotal + newSubtotal;

  const openTable = (n) => {
    setActiveTable(n);
    setCart([]);
    setEditingItemIdx(null);
    setDineOption("aqui");
  };

  const closeTable = () => {
    if (cart.length > 0 && !window.confirm("Tienes productos sin enviar. ¿Descartar?")) return;
    setActiveTable(null);
    setActiveOrder(null);
    setCart([]);
  };

  const addProduct = (p) => {
    setCart((c) => [...c, { product: p, qty: 1, variants: [], note: "" }]);
    setEditingItemIdx(cart.length);
  };

  const updateQty = (idx, delta) => {
    setCart((c) => c.map((it, i) => i === idx ? { ...it, qty: Math.max(1, it.qty + delta) } : it));
  };

  const removeItem = (idx) => {
    setCart((c) => c.filter((_, i) => i !== idx));
    if (editingItemIdx === idx) setEditingItemIdx(null);
  };

  const toggleVariant = (idx, opt) => {
    setCart((c) => c.map((it, i) => {
      if (i !== idx) return it;
      const has = it.variants.includes(opt);
      return { ...it, variants: has ? it.variants.filter((v) => v !== opt) : [...it.variants, opt] };
    }));
  };

  const submit = async () => {
    if (cart.length === 0) return;
    setSubmitting(true);
    try {
      const items = cart.map((it) => ({
        product_id: it.product.id,
        name: it.product.name,
        price: it.product.price,
        qty: it.qty,
        variants: it.variants,
        note: it.note || null,
        kitchen_station: it.product.kitchen_station,
      }));
      const r = await api.post("/orders", {
        table_number: activeTable,
        dine_option: dineOption,
        items,
      });
      const isAppend = !!activeOrder;
      toast.success(isAppend
        ? `Productos agregados a Orden #${pad(r.data.order_number)}`
        : `Orden #${pad(r.data.order_number)} enviada · Mesa ${activeTable}`);
      setCart([]);
      setEditingItemIdx(null);
      setActiveOrder(r.data);
      loadTables();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------- TABLES VIEW ----------------------------
  if (!activeTable) {
    const occupied = tables.filter((t) => t.status === "occupied");
    return (
      <div className="p-6 md:p-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-2">Tablero de Mesas</div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Selecciona una mesa</h1>
          </div>
          <div className="flex gap-3 text-sm">
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-zinc-200" /> Libre</div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-500" /> Ocupada</div>
          </div>
        </div>

        {/* Active orders summary */}
        {occupied.length > 0 && (
          <section className="mb-8" data-testid="mesero-active-orders">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3 flex items-center gap-2">
              <Receipt size={16} weight="bold" /> Mis órdenes activas ({occupied.length})
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {occupied.map((t) => (
                <button
                  key={t.number}
                  onClick={() => openTable(t.number)}
                  data-testid={`mesero-active-order-${t.number}`}
                  className="bg-white border-2 border-zinc-200 hover:border-brand rounded-2xl p-4 flex items-center gap-4 text-left transition-all hover:-translate-y-0.5"
                >
                  <div className="w-14 h-14 rounded-xl bg-red-500 text-white flex flex-col items-center justify-center font-display flex-shrink-0">
                    <div className="text-[9px] uppercase tracking-widest opacity-80">Mesa</div>
                    <div className="text-2xl font-black leading-none">{t.number}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base font-bold leading-tight">Orden #{pad(t.order_number || 0)}</div>
                    <div className="text-xs text-zinc-500 truncate">{t.items_count} producto(s) · {t.mesero_name}</div>
                  </div>
                  <div className="font-display text-xl font-black text-brand">${t.total}</div>
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">Mesas</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          {tables.map((t) => (
            <button
              key={t.number}
              onClick={() => openTable(t.number)}
              data-testid={`pos-table-${t.number}-card`}
              className={`aspect-square rounded-3xl flex flex-col items-center justify-center border-2 font-display transition-all ${
                t.status === "occupied"
                  ? "bg-red-500 border-red-600 text-white hover:bg-red-600 shadow-lg"
                  : "bg-white border-zinc-200 hover:border-brand text-zinc-900"
              }`}
            >
              <div className="text-xs uppercase tracking-[0.2em] opacity-80">Mesa</div>
              <div className="text-5xl font-black my-1">{t.number}</div>
              {t.status === "occupied" ? (
                <div className="text-xs opacity-90">#{pad(t.order_number || 0)} · ${t.total}</div>
              ) : (
                <div className="text-xs text-zinc-400">Libre</div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------------------------- ORDER BUILDER VIEW ----------------------------
  const existingItems = activeOrder?.items || [];

  return (
    <div className="grid lg:grid-cols-[1fr,440px] min-h-[calc(100vh-4rem)]">
      <section className="p-6 md:p-8 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <button onClick={closeTable} className="text-sm text-zinc-500 hover:text-brand inline-flex items-center gap-1" data-testid="pos-back-tables"><X size={16} /> Volver a mesas</button>
            <div className="flex items-baseline gap-3 mt-1 flex-wrap">
              <h2 className="font-display text-3xl font-black tracking-tight">Mesa {activeTable}</h2>
              {activeOrder && (
                <span className="font-display text-lg font-bold text-brand bg-red-50 px-3 py-1 rounded-lg" data-testid="mesero-order-number">
                  Orden #{pad(activeOrder.order_number)}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {[{ k: "aqui", l: "Comer aquí" }, { k: "llevar", l: "Para llevar" }].map((o) => (
              <button key={o.k} onClick={() => setDineOption(o.k)} data-testid={`pos-dine-${o.k}`}
                className={`px-4 h-11 rounded-xl text-sm font-semibold border-2 ${dineOption === o.k ? "bg-brand text-white border-brand" : "bg-white border-zinc-200"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pos-scroll pb-2 mb-4">
          {categories.map((c) => (
            <button key={c} onClick={() => setActiveCat(c)} data-testid={`pos-cat-${c.toLowerCase()}`}
              className={`px-4 h-10 rounded-full text-sm font-semibold whitespace-nowrap border-2 ${activeCat === c ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 hover:border-brand"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto pos-scroll grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pb-8">
          {filteredProducts.map((p) => (
            <button key={p.id} onClick={() => addProduct(p)} data-testid={`pos-add-product-${p.id}`}
              className="bg-white rounded-2xl border-2 border-zinc-200 hover:border-brand active:scale-95 transition-all overflow-hidden text-left">
              <div className="aspect-square bg-zinc-100 relative">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-4xl text-zinc-300">{p.category[0]}</div>
                )}
                {p.top_seller && <div className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Star size={10} weight="fill" /> Top</div>}
              </div>
              <div className="p-3">
                <div className="font-bold text-sm leading-tight line-clamp-2">{p.name}</div>
                <div className="font-display font-black text-brand text-lg mt-1">${p.price}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Cart panel */}
      <aside className="bg-white border-l border-zinc-200 flex flex-col max-h-[calc(100vh-4rem)]">
        <div className="p-5 border-b border-zinc-200">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">{activeOrder ? "Orden" : "Nuevo pedido"} · Mesa {activeTable}</div>
          <div className="font-display text-xl font-bold mt-1">
            {existingItems.length + cart.length} producto(s) total
          </div>
          {activeOrder && (
            <div className="text-xs text-zinc-500 mt-1">Orden #{pad(activeOrder.order_number)} · esperando pago</div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto pos-scroll p-3 space-y-2" data-testid="pos-cart-items">
          {/* Existing items (already sent) */}
          {existingItems.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold px-1 mb-2 flex items-center gap-1">
                <Clock size={11} weight="bold" /> Ya enviados ({existingItems.length})
              </div>
              {existingItems.map((it, i) => (
                <div key={it.item_id || i} className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 mb-1.5" data-testid={`mesero-existing-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm leading-tight">×{it.qty} {it.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">${(it.price * it.qty).toFixed(2)}</div>
                    </div>
                    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${
                      it.status === "ready" ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"
                    }`}>{it.status === "ready" ? "✓ Listo" : "En cocina"}</span>
                  </div>
                  {it.variants?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {it.variants.map((v) => (
                        <span key={v} className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-200 text-zinc-700 font-medium">{v}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* New (pending) items */}
          {cart.length > 0 && (
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand font-bold px-1 mb-2">
                Por enviar ({cart.length})
              </div>
              {cart.map((it, i) => (
                <div key={i} className={`rounded-2xl border-2 p-3 mb-1.5 ${editingItemIdx === i ? "border-brand bg-red-50/50" : "border-zinc-200 bg-white"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm leading-tight">{it.product.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">${it.product.price} · {it.product.kitchen_station}</div>
                    </div>
                    <button onClick={() => removeItem(i)} className="text-zinc-400 hover:text-red-500 p-1" data-testid={`pos-cart-remove-${i}`}>
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>

                  {it.variants.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {it.variants.map((v) => (
                        <span key={v} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-700 font-medium">{v}</span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQty(i, -1)} className="w-8 h-8 rounded-lg bg-white border border-zinc-200 inline-flex items-center justify-center" data-testid={`pos-cart-minus-${i}`}><Minus size={14} weight="bold" /></button>
                      <span className="font-bold w-6 text-center">{it.qty}</span>
                      <button onClick={() => updateQty(i, 1)} className="w-8 h-8 rounded-lg bg-white border border-zinc-200 inline-flex items-center justify-center" data-testid={`pos-cart-plus-${i}`}><Plus size={14} weight="bold" /></button>
                    </div>
                    <button onClick={() => setEditingItemIdx(editingItemIdx === i ? null : i)} className="text-xs text-brand font-semibold" data-testid={`pos-cart-variants-${i}`}>
                      {editingItemIdx === i ? "Listo" : "Variantes"}
                    </button>
                  </div>

                  {editingItemIdx === i && (
                    <div className="mt-3 pt-3 border-t border-zinc-200 space-y-3">
                      {VARIANT_GROUPS.map((g) => (
                        <div key={g.label}>
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">{g.label}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {g.options.map((opt) => (
                              <button key={opt} onClick={() => toggleVariant(i, opt)} data-testid={`pos-variant-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`variant-chip text-xs ${it.variants.includes(opt) ? "active" : ""}`}>
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {existingItems.length === 0 && cart.length === 0 && (
            <div className="text-center text-zinc-400 py-12 text-sm">Selecciona productos del menú</div>
          )}
        </div>

        <div className="p-5 border-t border-zinc-200 bg-white space-y-2">
          {activeOrder && cart.length > 0 && (
            <div className="flex justify-between text-xs text-zinc-500 font-semibold uppercase tracking-wider">
              <span>Subtotal previo</span><span>${existingTotal.toFixed(2)}</span>
            </div>
          )}
          {cart.length > 0 && (
            <div className="flex justify-between text-xs text-brand font-semibold uppercase tracking-wider">
              <span>+ Nuevo</span><span>${newSubtotal.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-baseline pt-1">
            <span className="text-sm text-zinc-500 font-semibold uppercase tracking-wide">Total</span>
            <span className="font-display font-black text-3xl text-brand">${grandTotal.toFixed(2)}</span>
          </div>
          <button onClick={submit} disabled={cart.length === 0 || submitting} data-testid="pos-send-order"
            className="btn-brand w-full h-16 inline-flex items-center justify-center gap-2 text-lg disabled:opacity-50">
            {submitting ? "Enviando..." : (
              <><PaperPlaneTilt size={22} weight="fill" /> {activeOrder ? "Agregar a Cocina" : "Enviar a Cocina"}</>
            )}
          </button>
        </div>
      </aside>
    </div>
  );
}
